import { Component, Inject } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DisputeCaseBaseDto } from "src/app/shared/models/dispute-resolution/dispute-base-models";
import { NotificationService } from "src/app/shared/services/notification.service";
import { DataListFacilitationMeetingType } from "src/app/shared/models/data-list";
import { DataListService } from "src/app/shared/services/data-list.service";
import {
  CreateUpdateIEPFacilitationMeetingDto,
  IEPFacilitationDisputeMeetingDto,
} from "src/app/shared/models/dispute-resolution/iep-facilitation-models";

@Component({
  selector: "app-add-edit-meeting-information",
  templateUrl: "./add-edit-meeting-information.component.html",
  styleUrls: ["./add-edit-meeting-information.component.scss"],
  standalone: false,
})
export class AddEditMeetingInformationComponent {
  form: FormGroup;

  disputeCase: DisputeCaseBaseDto;
  meetingTypeOptions: DataListFacilitationMeetingType[];
  meeting: IEPFacilitationDisputeMeetingDto;

  // The modal is reused for both IEP Facilitation "Meetings" and Due Process "Hearings".
  // `noun` swaps the displayed wording; `showSurveys` hides the survey checkbox for hearings
  // (no surveys are sent out for hearings).
  noun: string = "Meeting";
  showSurveys: boolean = true;

  isEditMode: boolean = false;

  constructor(
    private notificationService: NotificationService,
    private dataListService: DataListService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddEditMeetingInformationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { disputeCase: DisputeCaseBaseDto; meetingTypeOptions: DataListFacilitationMeetingType[]; meeting: IEPFacilitationDisputeMeetingDto; noun?: string; showSurveys?: boolean }
  ) {
    this.disputeCase = data.disputeCase || null;
    this.meetingTypeOptions = data.meetingTypeOptions || [];
    this.meeting = data.meeting || null;
    this.noun = data.noun || "Meeting";
    this.showSurveys = data.showSurveys !== undefined ? data.showSurveys : true;
  }

  ngOnInit() {
    this.isEditMode = this.meeting && this.meeting.id ? true : false;

    this.form = this.fb.group({
      dateOfScheduledMeeting: [
        this.meeting?.dateOfScheduledMeeting
          ? new Date(this.meeting.dateOfScheduledMeeting)
          : null,
      ],
      facilitationMeetingTypeId: [
        this.meeting?.facilitationMeetingTypeId || "",
      ],
      timeAndDurationOfFacilitationMeeting: [
        this.meeting?.timeAndDurationOfFacilitationMeeting || "",
      ],
      locationOfFacilitationMeeting: [
        this.meeting?.locationOfFacilitationMeeting || "",
      ],
      wasMeetingHeld: [this.meeting?.wasMeetingHeld || false],
      faciltiatorSentOutSurveyToParticipants: [
        this.meeting?.faciltiatorSentOutSurveyToParticipants || false,
      ],
      notes: [this.meeting?.notes || ""],
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.form.valid) {
      // Check if all form fields are empty before mapping
      const values = this.form.getRawValue();
      const isEmpty = Object.values(values).every(
        (v) => v === undefined || v === null || v === "" || v === false
      );
      if (isEmpty) {
        this.notificationService.error(
          "Cannot save an empty meeting. Please fill out at least one field."
        );
        return;
      }
      const meetingData = this.mapMeetingPayloadFromForm();
      this.dialogRef.close(meetingData);
    } else {
      this.form.markAllAsTouched();
      this.notificationService.error("Please fix errors before saving.");
    }
  }

  private mapMeetingPayloadFromForm(): CreateUpdateIEPFacilitationMeetingDto {
    return {
      id: this.meeting?.id || undefined,
      disputeCaseId: this.disputeCase.id,
      dateOfScheduledMeeting: this.form.value.dateOfScheduledMeeting,
      facilitationMeetingTypeId: this.form.value.facilitationMeetingTypeId,
      facilitationMeetingType: this.meetingTypeOptions.find(
        (m) => m.id === this.form.value.facilitationMeetingTypeId
      ),
      timeAndDurationOfFacilitationMeeting:
        this.form.value.timeAndDurationOfFacilitationMeeting,
      locationOfFacilitationMeeting:
        this.form.value.locationOfFacilitationMeeting,
      wasMeetingHeld: this.form.value.wasMeetingHeld,
      faciltiatorSentOutSurveyToParticipants:
        this.form.value.faciltiatorSentOutSurveyToParticipants,
      notes: this.form.value.notes,
    } as CreateUpdateIEPFacilitationMeetingDto;
  }
}
